// cspell:ignore topup topupmalformed transfermalformed instantpaymenttopuprequesttransitionedtomalformed instantpaymenttransferrequesttransitionedtomalformed
import { JalaliDateHelper } from '@tools';
import { KALA_WALLET_TITLES, KalaWalletKey } from '../../../models/wallet.model';
import {
	InstantPaymentResponse,
	WalletEventResponse,
	WalletTransferRequestResponse
} from '../../../models/wallet-response.model';
import {
	GATEWAY_DEPOSIT_CHARGED_STATES,
	GATEWAY_DEPOSIT_EVENTS,
	GATEWAY_DEPOSIT_STOPPED_NOTICE,
	GATEWAY_DEPOSIT_OUTCOME_METAS,
	GATEWAY_DEPOSIT_STAGES,
	TRANSFER_BANK_STEP_TITLE,
	TRANSFER_BROKER_STEP_TITLE,
	TRANSFER_CHARGE_MAIN_STEP_TITLE,
	TRANSFER_DATE_FORMAT,
	TRANSFER_EVENT_STEP_TITLES,
	TRANSFER_GATEWAY_SETTLED_STEP_TITLES,
	TRANSFER_GATEWAY_STEP_TITLE,
	TRANSFER_REQUEST_STEP_TITLE,
	TRANSFER_SENT_EVENTS,
	TRANSFER_SENT_TO_KALA_STATE,
	TRANSFER_SENT_TO_KALA_STATE_META,
	TRANSFER_STATE_METAS,
	TRANSFER_TIME_FORMAT,
	TRANSFER_TO_MAIN_STEP_TITLE,
	TRANSFER_WALLET_TITLES
} from './wallet-transfer-history.constants';
import {
	GatewayDepositProgress,
	TransferHistoryDay,
	TransferHistoryItem,
	TransferStateKey,
	WalletTransferKind,
	WalletTransferStateMeta,
	WalletTransferStep,
	WalletTransferStepStatus,
	WalletTransferTimeline
} from './wallet-transfer-history.model';

/** A transfer request as the history lists it and the bottom sheet opens it. */
export function toTransferHistoryItem(
	request: WalletTransferRequestResponse,
	currentWallet: KalaWalletKey
): TransferHistoryItem {
	const from = normalizeWalletKey(request.from);
	const to = normalizeWalletKey(request.to);
	const kind = transferKind(request, to, currentWallet);
	const state = transferStateMeta(request.state);
	const dateLabel = formatTransferDate(request.createdDate);
	const title = historyTitle(request.from, from, request.to, to, request.initiator);
	const toTitle = request.to === 'Hami' ? 'Hami' : undefined;
	const item: TransferHistoryItem = {
		id: request.id,
		apiId: request.id,
		source: 'transferRequest',
		amount: request.amount,
		state: request.state,
		createdDate: request.createdDate,
		kind,
		title,
		stateLabel: state.label,
		stateIcon: state.icon,
		stateClass: state.className,
		stateTip: state.tip,
		dateLabel,
		stepTitles: transferStepTitles(kind, from, to, toTitle),
		timeline: buildTransferTimeline(kind, from, to, request, dateLabel, toTitle, title)
	};

	return kind === 'depositGateway' ? withGatewayDepositProgress(item, []) : item;
}

/**
 * A gateway top-up as the history lists it.
 *
 * The endpoint names no wallet: `POST /wallet/charge` charges کیف اصلی, and the money is
 * moved on from there to the trader's کیف پیش‌فرض, which is what the row and the timeline
 * both say. The vendor leaves `id` null until it numbers the payment, so one is made for
 * the row out of what the payment does carry.
 */
export function toInstantPaymentHistoryItem(
	payment: InstantPaymentResponse,
	currentWallet: KalaWalletKey
): TransferHistoryItem {
	const state = transferStateMeta(payment.state);
	const dateLabel = formatTransferDate(payment.createdDate);
	const title = `واریز به ${KALA_WALLET_TITLES[currentWallet]}`;

	return withGatewayDepositProgress(
		{
			id: payment.id ?? `instant-payment-${payment.createdDate}-${payment.amount}`,
			apiId: payment.id,
			source: 'instantPayment',
			amount: payment.amount,
			state: payment.state,
			createdDate: payment.createdDate,
			kind: 'depositGateway',
			title,
			stateLabel: state.label,
			stateIcon: state.icon,
			stateClass: state.className,
			stateTip: state.tip,
			dateLabel,
			stepTitles: transferStepTitles('depositGateway', 'Main', currentWallet),
			timeline: buildTransferTimeline(
				'depositGateway',
				'Main',
				currentWallet,
				payment,
				dateLabel,
				undefined,
				title
			)
		},
		[]
	);
}

/**
 * Which of the handoff's sheets a request opens, read from the wallet the money is going
 * into: arriving in the trader's own wallet is a deposit, anywhere else is on its way out.
 *
 * The kind the API cannot tell apart yet — one from the سهام system, a withdrawal to a bank
 * account or to the broker — is the endpoint's to send; until it does, a transfer request
 * is a move between two of the customer's own wallets, and a gateway top-up is the one
 * other kind the history can name, read from its own endpoint.
 */
export function resolveTransferKind(to: KalaWalletKey, currentWallet: KalaWalletKey): WalletTransferKind {
	return to === currentWallet ? 'depositWallet' : 'withdrawWallet';
}

/**
 * A request's kind: a gateway top-up by its initiator, money the customer sends out to
 * حساب سهام as a withdrawal — `Hami` has no card here, so read by its wallet alone it would
 * pass for the current wallet — and anything else by the wallet it goes into.
 */
function transferKind(
	request: Pick<WalletTransferRequestResponse, 'initiator' | 'to'>,
	to: KalaWalletKey,
	currentWallet: KalaWalletKey
): WalletTransferKind {
	if (request.initiator === 'InstantPayment') {
		return 'depositGateway';
	}

	return request.initiator === 'User' && request.to === 'Hami'
		? 'withdrawBroker'
		: resolveTransferKind(to, currentWallet);
}

/** How a request's state reads: on the row's chip, and on the stop it belongs to. */
export function transferStateMeta(state: string): WalletTransferStateMeta {
	return isSentToKalaState(state) ? TRANSFER_SENT_TO_KALA_STATE_META : TRANSFER_STATE_METAS[transferStateKey(state)];
}

function isSentToKalaState(state: string): boolean {
	return state.toLowerCase().replace(/[^a-z]/g, '') === TRANSFER_SENT_TO_KALA_STATE;
}

/**
 * Which of the states a record can be in a `state` string names.
 *
 * The contract spells the same state on the record and inside the event that moved it there
 * — `Malformed` and `TransferRequestTransitionedToMalformed` — so the string is matched by
 * the words it carries rather than by an exact value. Anything unrecognised is a record still
 * on its way, which is the reading that loses the least.
 */
export function transferStateKey(state: string): TransferStateKey {
	const normalized = state.toLowerCase().replace(/[^a-z]/g, '');

	if (normalized.includes('malformed')) return 'malformed';
	if (normalized.includes('cancel')) return 'canceled';
	if (
		normalized.includes('failed') ||
		normalized.includes('rejected') ||
		normalized.includes('error') ||
		normalized.includes('reverted') ||
		normalized.includes('invalid') ||
		normalized.includes('maximumattempts')
	) {
		return 'failed';
	}
	if (
		normalized.includes('completed') ||
		normalized.includes('success') ||
		normalized.includes('done') ||
		normalized.includes('succeeded') ||
		normalized.includes('settled') ||
		normalized.includes('finalized')
	) {
		return 'completed';
	}

	return 'pending';
}

/**
 * The timeline a transfer of `kind` is drawn as: the stops it passes through, and where the
 * request has got to.
 *
 * The API reports one state for the whole request rather than one per stop, so every stop but
 * the last counts as behind it — the last is the one the money is waiting on.
 *
 * That one state is also the only date the endpoint sends, and it is the date the request
 * was registered on, so it is printed on the stop it belongs to and nowhere else: the other
 * stops read their own times off their own events, and until those arrive they claim none.
 */
export function buildTransferTimeline(
	kind: WalletTransferKind,
	from: KalaWalletKey,
	to: KalaWalletKey,
	request: Pick<WalletTransferRequestResponse, 'amount' | 'state'>,
	dateLabel: string,
	toTitle?: 'Hami',
	timelineTitle?: string
): WalletTransferTimeline {
	const titles = transferStepTitles(kind, from, to, toTitle);

	const steps = titles.map((title, index): WalletTransferStep => {
		const status = transferStepStatus(request.state, index, titles.length);

		return {
			title,
			status,
			dateLabel: status === 'done' && index === 0 ? dateLabel : null,
			amount: index === 0 ? request.amount : null
		};
	});

	return { title: timelineTitle ?? steps[1]?.title ?? '', steps };
}

/**
 * Keeps the request timeline canonical.
 *
 * The events endpoint reports the complete retry history. Show one applying event on the
 * timeline even when a retry produced several, and keep the remaining timeline steps aligned
 * with the record's final state.
 */
export function applyTransferEvents(item: TransferHistoryItem, events: WalletEventResponse[]): TransferHistoryItem {
	if (!events.length) {
		return item;
	}

	if (item.kind === 'depositGateway') {
		return withGatewayDepositProgress(item, events);
	}

	const applyingEventType = 'transferrequesttransitionedtoapplying';
	const failedEventType = 'transferrequesttransitionedtofailed';
	const hamiCreatedEventType = 'hamitransferrequestcreated';
	const eventType = (event: WalletEventResponse): string => eventName(event.eventType);
	const lastApplyingEvent = [...events].reverse().find(event => eventType(event) === applyingEventType);
	let applyingSeen = false;
	const visibleEvents = events
		.filter(event => {
			const type = eventType(event);
			if (type === hamiCreatedEventType) return false;
			if (type === failedEventType) return false;
			if (type !== applyingEventType) return true;
			if (applyingSeen) return false;
			applyingSeen = true;
			return true;
		})
		.map(event => (eventType(event) === applyingEventType && lastApplyingEvent ? lastApplyingEvent : event));
	// `Failed` is the record between two retries: its last stop is still ahead, not lost.
	const betweenRetries = transferStateKey(item.state) === 'failed';
	const steps = visibleEvents.map((event, index): WalletTransferStep => {
		const eventKey = eventType(event);
		const isLast = index === visibleEvents.length - 1;
		const eventFailed =
			eventKey.includes('failed') || eventKey.includes('malformed') || eventKey.includes('cancel');
		// The request reaching بورس کالا is a stop behind it, even while the answer is awaited.
		const status =
			isCompletedTransferState(item.state) || TRANSFER_SENT_EVENTS.has(eventKey)
				? 'done'
				: eventFailed
					? 'failed'
					: isLast
						? betweenRetries
							? 'upcoming'
							: isFailedTransferState(item.state)
								? 'failed'
								: 'current'
						: 'done';

		return {
			title: TRANSFER_EVENT_STEP_TITLES[eventKey] ?? item.timeline.steps[index]?.title ?? event.eventType,
			status,
			dateLabel: formatTransferDate(event.occurredAt),
			amount: index === 0 ? item.amount : null
		};
	});
	return {
		...item,
		timeline: { ...item.timeline, steps }
	};
}

/**
 * The stops a record has reached, read from its own events — shared by the history's detail
 * sheet and the gateway result sheet, which show the same stops from two different doors.
 *
 * A stop with an event behind it has happened and carries that event's time; the stop after
 * them is the one the money waits on, and anything past it is not drawn at all, so the sheet
 * grows stop by stop as the record moves. `state` closes the whole timeline when it says the
 * record is through, and stops it where it got to when it says it failed.
 *
 * Without any events the stops are handed back as they came.
 */
export function drawStepsFromEvents(
	steps: readonly WalletTransferStep[],
	events: readonly WalletEventResponse[],
	state: string
): WalletTransferStep[] {
	const reached = Math.min(events.length, steps.length);

	if (!reached) {
		return [...steps];
	}

	const completed = isCompletedTransferState(state);
	const failed = !completed && isFailedTransferState(state);
	const drawn = completed ? steps.length : failed ? reached : Math.min(reached + 1, steps.length);

	return steps.slice(0, drawn).map((step, index): WalletTransferStep => {
		const event = events[index];

		if (completed) {
			return {
				...step,
				status: 'done',
				dateLabel: event ? formatTransferDate(event.occurredAt) : step.dateLabel
			};
		}

		// The stop the record stopped at carries no time: it is the one that never finished.
		if (failed) {
			return index === drawn - 1
				? { ...step, status: 'failed', dateLabel: null }
				: { ...step, status: 'done', dateLabel: event ? formatTransferDate(event.occurredAt) : step.dateLabel };
		}

		return event
			? { ...step, status: 'done', dateLabel: formatTransferDate(event.occurredAt) }
			: { ...step, status: 'current', dateLabel: null };
	});
}

/**
 * An event's bare class name, lowercased — the key every event table here is read by.
 *
 * The endpoint sends the type's assembly-qualified name —
 * `easy.kala.wallet.domain.Wallet.Transfer.Events.TransferRequestCreated, easy.kala.wallet.domain, Version=…`
 * — so the assembly is cut at the first comma and the namespace at the last dot. A bare name
 * passes through as it is.
 */
export function eventName(eventType: string): string {
	const typeName = eventType.split(',')[0].trim();

	return typeName.slice(typeName.lastIndexOf('.') + 1).toLowerCase();
}

/**
 * Where each stage of a gateway deposit stands, read from the record's state and, once they
 * are read, its events.
 *
 * - The gateway has taken the money once the record is past `Created`: کیف اصلی is charged
 *   (`Pending` and on), or charging it stopped.
 * - `InstantPaymentTopupMalformed` and `InstantPaymentTransferMalformed` both stop the deposit
 *   at charging کیف اصلی — the transfer they name is the one into بورس کالا. Both are final —
 *   support takes over — and the deposit reads «خطا در تراکنش». A bare `Malformed` names no
 *   stage, so the stage the deposit had reached is the one it stopped at. A move never
 *   reached because charging stopped is still drawn, as `upcoming`.
 * - Canceled, or failed before کیف اصلی was charged, is the gateway refusing the payment.
 *   `Failed` alone is not: it is the record between two retries of the move.
 */
export function gatewayDepositProgress(state: string, events: readonly WalletEventResponse[]): GatewayDepositProgress {
	const normalized = state.toLowerCase().replace(/[^a-z]/g, '');
	const key = transferStateKey(state);
	const firstAt = (...names: string[]): string | null =>
		events.find(event => names.includes(eventName(event.eventType)))?.occurredAt ?? null;
	const seen = (...names: string[]): boolean => events.some(event => names.includes(eventName(event.eventType)));
	const {
		created,
		pending,
		applying,
		transferFailed,
		completed: completedEvent,
		malformed,
		topupMalformed,
		transferMalformed
	} = GATEWAY_DEPOSIT_EVENTS;

	const topupStopped = normalized.includes('topupmalformed') || seen(topupMalformed);
	const transferStoppedOutright = normalized.includes('transfermalformed') || seen(transferMalformed);
	const completed = !transferStoppedOutright && (key === 'completed' || seen(completedEvent));
	const charged =
		completed ||
		transferStoppedOutright ||
		GATEWAY_DEPOSIT_CHARGED_STATES.has(normalized) ||
		seen(pending, applying, transferFailed);
	const chargeStopped = topupStopped || transferStoppedOutright || (key === 'malformed' && !charged);
	const transferStopped = !chargeStopped && key === 'malformed' && charged;
	const gatewayFailed = !charged && !chargeStopped && (key === 'failed' || key === 'canceled');
	// The event that stopped it, else the one the state is spelled after.
	const stoppedEvent = [...events]
		.reverse()
		.map(event => eventName(event.eventType))
		.find(name => name === topupMalformed || name === transferMalformed || name === malformed);
	const stoppedBy = !(chargeStopped || transferStopped)
		? null
		: (stoppedEvent ??
			(topupStopped ? topupMalformed : normalized.includes('transfermalformed') ? transferMalformed : null));

	return {
		outcome: gatewayFailed
			? 'depositFailed'
			: chargeStopped || transferStopped
				? 'incomplete'
				: completed
					? 'completed'
					: 'inProgress',
		stages: {
			gateway: gatewayFailed ? 'failed' : charged || chargeStopped ? 'done' : 'current',
			chargeMain: chargeStopped ? 'failed' : charged ? 'done' : null,
			transfer: transferStopped
				? 'failed'
				: completed
					? 'done'
					: chargeStopped
						? 'upcoming'
						: charged
							? 'current'
							: null
		},
		occurredAt: {
			gateway: firstAt(created),
			chargeMain: chargeStopped
				? firstAt(topupMalformed, transferMalformed, malformed)
				: firstAt(pending, applying),
			transfer: completed ? firstAt(completedEvent) : transferStopped ? firstAt(malformed) : null
		},
		stoppedBy
	};
}

/**
 * A gateway deposit with its chip and its timeline read from where its stages stand.
 *
 * The timeline grows stage by stage: the stages the deposit has not reached are not drawn.
 * A stage carries the time of the event that settled it; the gateway's own, until its event
 * is read, carries the date the record was registered on.
 */
function withGatewayDepositProgress(
	item: TransferHistoryItem,
	events: readonly WalletEventResponse[]
): TransferHistoryItem {
	const progress = gatewayDepositProgress(item.state, events);
	const meta =
		progress.outcome === 'inProgress' && isSentToKalaState(item.state)
			? TRANSFER_SENT_TO_KALA_STATE_META
			: GATEWAY_DEPOSIT_OUTCOME_METAS[progress.outcome];
	const steps = GATEWAY_DEPOSIT_STAGES.flatMap((stage, index): WalletTransferStep[] => {
		const status = progress.stages[stage];

		if (!status) {
			return [];
		}

		const occurredAt = progress.occurredAt[stage];
		const dateLabel =
			status === 'current' || status === 'upcoming'
				? null
				: occurredAt
					? formatTransferDate(occurredAt)
					: index === 0
						? item.dateLabel
						: null;

		// The gateway's stop only reads «واریز موفق» once the money is past it; a later stop that
		// failed says what failed, in the words of the event that stopped it.
		const stoppedTitle =
			status === 'failed' && progress.stoppedBy ? TRANSFER_EVENT_STEP_TITLES[progress.stoppedBy] : undefined;
		const title =
			stage === 'gateway' && (status === 'done' || status === 'failed')
				? TRANSFER_GATEWAY_SETTLED_STEP_TITLES[status]
				: (stoppedTitle ?? item.stepTitles[index] ?? '');

		return [{ title, status, dateLabel, amount: index === 0 ? item.amount : null }];
	});

	return {
		...item,
		stateLabel: meta.label,
		stateIcon: meta.icon,
		stateClass: meta.className,
		stateTip: meta.tip,
		timeline: { ...item.timeline, steps },
		notice: progress.outcome === 'incomplete' ? GATEWAY_DEPOSIT_STOPPED_NOTICE : undefined
	};
}

/** The stops a transfer of each kind passes through, in the handoff's own words. */
export function transferStepTitles(
	kind: WalletTransferKind,
	from: KalaWalletKey,
	to: KalaWalletKey,
	toTitle?: 'Hami'
): string[] {
	const destinationTitle = TRANSFER_WALLET_TITLES[toTitle ?? to];

	switch (kind) {
		case 'depositGateway':
			return [
				TRANSFER_GATEWAY_STEP_TITLE,
				TRANSFER_CHARGE_MAIN_STEP_TITLE,
				`انتقال از ${TRANSFER_WALLET_TITLES.Main} به ${destinationTitle}`
			];
		case 'depositBroker':
			return [
				TRANSFER_REQUEST_STEP_TITLE,
				TRANSFER_CHARGE_MAIN_STEP_TITLE,
				`انتقال از ${TRANSFER_WALLET_TITLES.Main} به ${destinationTitle}`
			];
		case 'withdrawBank':
			return [TRANSFER_REQUEST_STEP_TITLE, TRANSFER_TO_MAIN_STEP_TITLE, TRANSFER_BANK_STEP_TITLE];
		case 'withdrawBroker':
			return [TRANSFER_REQUEST_STEP_TITLE, TRANSFER_TO_MAIN_STEP_TITLE, TRANSFER_BROKER_STEP_TITLE];
		case 'depositWallet':
			return [TRANSFER_REQUEST_STEP_TITLE, `انتقال از ${TRANSFER_WALLET_TITLES[from]} به ${destinationTitle}`];
		case 'withdrawWallet':
			return [TRANSFER_REQUEST_STEP_TITLE, `انتقال از ${TRANSFER_WALLET_TITLES[from]} به ${destinationTitle}`];
	}
}

/** Where a stop stands, given the one state the request came with. */
function transferStepStatus(state: string, index: number, count: number): WalletTransferStepStatus {
	if (isCompletedTransferState(state) || index < count - 1) {
		return 'done';
	}

	return isFailedTransferState(state) ? 'failed' : 'current';
}

function isCompletedTransferState(state: string): boolean {
	return transferStateKey(state) === 'completed';
}

function isFailedTransferState(state: string): boolean {
	const key = transferStateKey(state);

	return key === 'failed' || key === 'malformed' || key === 'canceled';
}

/** How the row names a transfer: the side the trader's own wallet is on, or both wallets. */
function historyTitle(
	rawFrom: string,
	from: KalaWalletKey,
	rawTo: string,
	to: KalaWalletKey,
	initiator: string
): string {
	const source = rawFrom === 'Hami' ? TRANSFER_WALLET_TITLES.Hami : KALA_WALLET_TITLES[from];
	const destination = rawTo === 'Hami' ? TRANSFER_WALLET_TITLES.Hami : KALA_WALLET_TITLES[to];

	if (initiator === 'InstantPayment') {
		return `واریز به ${destination}`;
	}

	return initiator === 'User' && rawTo === 'Hami'
		? `برداشت به ${destination}`
		: `انتقال از ${source} به ${destination}`;
}

/** The wallet an API name points at; anything this build has no card for reads as گواهی سپرده. */
export function normalizeWalletKey(value: string): KalaWalletKey {
	const normalized = value.toLowerCase();

	if (normalized === 'cdc' || normalized === 'certificateofdeposit') return 'CDC';
	if (normalized === 'main') return 'Main';
	if (normalized === 'option') return 'Option';
	if (normalized === 'future') return 'Future';
	if (normalized === 'warehouse' || normalized === 'warehousewallet') return 'WareHouse';

	return 'CDC';
}

/**
 * The full history's days, in the order the rows already stand — newest first — each under
 * the Jalali date its transfers were made on.
 */
export function groupTransfersByDay(requests: readonly TransferHistoryItem[]): TransferHistoryDay[] {
	const days = new Map<string, TransferHistoryDay>();

	for (const request of requests) {
		const date = JalaliDateHelper.getFormattedJDate(new Date(request.createdDate)) ?? '';
		const day = days.get(date);

		if (day) {
			day.requests.push(request);
		} else {
			days.set(date, { date, requests: [request] });
		}
	}

	return [...days.values()];
}

/** The request's own date, as the row and every stop behind it print it. */
export function formatTransferDate(value: string): string {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return `${TRANSFER_DATE_FORMAT.format(date)} - ${TRANSFER_TIME_FORMAT.format(date)}`;
}
